import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useArticles } from '../hooks/useArticles';
import { useBatchStatusPolling } from '../hooks/useBatchStatusPolling';
import { useToast } from '../hooks/useToast';
import { setTitle } from '../utils/environment';
import AddUrlForm from '../components/AddUrlForm';
import ArticleCard from '../components/ArticleCard';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import SearchModal from '../components/SearchModal';
import TopicsWidget from '../components/TopicsWidget';

const Dashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [savedSearchState, setSavedSearchState] = useState(null);
  const previousArticles = useRef([]);
  const fetchArticlesRef = useRef();
  const searchInputRef = useRef();
  const statsRefreshTimeoutRef = useRef(null);

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const {
    articles,
    stats,
    pagination,
    filters,
    isLoading,
    error,
    fetchArticles,
    fetchStats,
    setFilters,
    clearError,
    refreshArticles,
    goToPage,
    setPerPage,
    isEmpty,
  } = useArticles();

  // Enable batch status polling for pending/processing articles
  useBatchStatusPolling(3000, true);

  // Keep a stable reference to fetchArticles
  useEffect(() => {
    fetchArticlesRef.current = fetchArticles;
  }, [fetchArticles]);

  // Calculate unread count from stats (total across all pages), fallback to current page
  const unreadCount = stats?.unread_articles ?? articles.filter(article => !article.is_read).length;

  // Load articles on mount only
  useEffect(() => {
    // Set page title with environment prefix
    setTitle('Read It Never');

    fetchArticles({ page: 1 }).catch(console.error);

    // Cleanup stats refresh timeout on unmount
    return () => {
      if (statsRefreshTimeoutRef.current) {
        clearTimeout(statsRefreshTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Background stats refresh when returning to dashboard
  useEffect(() => {
    // Always refresh stats when dashboard mounts to ensure up-to-date numbers
    // This is particularly important when returning from article viewer
    // where user may have marked articles as read/unread
    fetchStats(true).catch(console.error); // force=true for fresh data
  }, []); // Empty dependency - runs once on mount

  // Check for returning from search result and restore modal state
  useEffect(() => {
    const searchState = sessionStorage.getItem('searchModalState');
    if (searchState) {
      try {
        const parsedState = JSON.parse(searchState);
        setSavedSearchState(parsedState);
        setIsSearchModalOpen(true);
        // Clear from session storage to prevent reopening on future visits
        sessionStorage.removeItem('searchModalState');
      } catch (error) {
        console.error('Failed to restore search state:', error);
        sessionStorage.removeItem('searchModalState');
      }
    }
  }, []);

  // Update title when unread count changes
  useEffect(() => {
    if (unreadCount > 0) {
      setTitle(`Read It Never (${unreadCount})`);
    } else {
      setTitle('Read It Never');
    }
  }, [unreadCount, stats]);

  // Check for newly completed articles and show notifications
  useEffect(() => {
    if (previousArticles.current.length > 0) {
      articles.forEach(article => {
        const previousArticle = previousArticles.current.find(prev => prev.id === article.id);
        if (previousArticle &&
            previousArticle.processing_status !== 'completed' &&
            article.processing_status === 'completed' &&
            article.title && article.title !== '') {
          showSuccess(`"${article.title}" finished processing!`);
        }
        if (previousArticle &&
            previousArticle.processing_status !== 'failed' &&
            article.processing_status === 'failed') {
          showError(`Failed to process article: ${article.url}`);
        }
      });
    }
    previousArticles.current = articles;
  }, [articles, showSuccess, showError]);

  // Update filters with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== filters.search || domainFilter !== filters.domain) {
        setFilters({
          search: searchQuery,
          domain: domainFilter,
        });
        // Only reset to page 1 when filters actually change, not when pagination changes
        fetchArticlesRef.current?.({ page: 1 }).catch(console.error);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, domainFilter, filters.search, filters.domain, setFilters]);

  const handleArticleAdded = useCallback(() => {
    // Show success message
    showSuccess('Article saved! Processing in background...');

    // Debounced stats refresh - cancel existing timeout if any
    if (statsRefreshTimeoutRef.current) {
      clearTimeout(statsRefreshTimeoutRef.current);
    }
    statsRefreshTimeoutRef.current = setTimeout(() => {
      fetchStats().catch(console.error);
      statsRefreshTimeoutRef.current = null;
    }, 100);

    // Optionally refresh the article list if needed
    if (articles.length === 0) {
      refreshArticles().catch(console.error);
    }
  }, [fetchStats, articles.length, refreshArticles, showSuccess]);

  const handleArticleDeleted = useCallback(() => {
    // Debounced stats refresh - cancel existing timeout if any
    if (statsRefreshTimeoutRef.current) {
      clearTimeout(statsRefreshTimeoutRef.current);
    }
    statsRefreshTimeoutRef.current = setTimeout(() => {
      fetchStats().catch(console.error);
      statsRefreshTimeoutRef.current = null;
    }, 100);
  }, [fetchStats]);

  const handleRefresh = useCallback(() => {
    refreshArticles().catch(console.error);
    // Force refresh stats
    fetchStats(true).catch(console.error);
  }, [refreshArticles, fetchStats]);

  const handlePageChange = useCallback((page) => {
    goToPage(page).catch(console.error);
  }, [goToPage]);

  const handlePerPageChange = useCallback((perPage) => {
    setPerPage(perPage).catch(console.error);
  }, [setPerPage]);

  const clearSearch = () => {
    setSearchQuery('');
    setDomainFilter('');
  };

  const handleOpenSearchModal = () => {
    setIsSearchModalOpen(true);
  };

  const handleCloseSearchModal = () => {
    setIsSearchModalOpen(false);
    // Notify search input that modal was just closed to prevent immediate reopen
    if (searchInputRef.current?.notifyModalClosed) {
      searchInputRef.current.notifyModalClosed();
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-8">
              <div className="flex items-center">
                <div className="h-10 w-10 mr-3">
                  <img src={new URL('../assets/logo.png', import.meta.url).href} alt="Read It Never" className="h-10 w-10 rounded-xl" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Read It Never</h1>
                  <p className="text-sm text-gray-600">Reading is slow. Just vibe it.</p>
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="flex items-center space-x-6">
                <Link
                  to="/topics"
                  className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Topics
                </Link>
                <Link
                  to="/highlights"
                  className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  Highlights
                </Link>
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.email}
              </span>
              <button
                onClick={logout}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 cursor-pointer"
            onClick={() => navigate('/stats')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                navigate('/stats');
              }
            }}
          >
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Articles</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.total_articles || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Unread</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.unread_articles || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Read</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.read_articles || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Weekly Reads</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.weekly_read_articles || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add URL Form */}
        <div className="mb-8">
          <AddUrlForm onSuccess={handleArticleAdded} />
        </div>

        {/* Topics Widget */}
        <TopicsWidget />

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput 
                ref={searchInputRef}
                onSearchClick={handleOpenSearchModal}
                placeholder="Search with AI"
              />
            </div>
            
            <div className="flex items-center space-x-3">
              
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
              >
                <svg className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={clearError}
                    className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Articles Grid */}
        {isLoading && articles.length === 0 ? (
          <div className="text-center py-12">
            <div className="spinner w-8 h-8 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading articles...</p>
          </div>
        ) : isEmpty ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No articles yet</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || domainFilter 
                ? 'No articles match your current filters.'
                : 'Start by adding your first article using the form above.'
              }
            </p>
            {(searchQuery || domainFilter) && (
              <button
                onClick={clearSearch}
                className="text-primary-600 hover:text-primary-500 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {articles.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  onDelete={handleArticleDeleted}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                isLoading={isLoading}
                className="mt-8"
              />
            )}
          </>
        )}
      </main>

      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchModalOpen}
        onClose={handleCloseSearchModal}
        savedSearchState={savedSearchState}
        onSearchStateCleared={() => setSavedSearchState(null)}
      />
    </div>
  );
};

export default Dashboard;